import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useMonths } from "../hooks/useMonths";
import { Users } from "../api/models/user.models";
import { Company, Organization } from "../api/models/companies.models";
import { ReportStatus } from "../api/models/report.models";
import { AdminReportFilters, UIState } from "../types/ui-types";
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
} from "primereact/autocomplete";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";

const FilterContainer = styled.div`
  margin-bottom: 2rem;
  margin-left: 1rem;
`;

const FilterToggle = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 0.5rem;
  color: #333;
  font-weight: 500;
  max-width: fit-content;
  padding: 0.5rem 1rem;
  background-color: var(--sumi-blue);
  color: var(--sumi-text-primary);
  border-radius: var(--border-radius);

  &:hover {
    background-color: var(--sumi-darker-blue);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--sumi-darker-blue);
  }
`;

const ChevronIcon = styled.i<{ $isOpen: boolean }>`
  transform: ${(props) => (props.$isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease;
`;
const StyledAutoComplete = styled(AutoComplete)`
  width: 100%;
  min-width: 330px;

  .p-autocomplete-input {
    width: 100%;
  }
`;

const AutoCompleteWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius);
  transition: all 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
    color: #333;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  i {
    font-size: 1rem;
  }
`;

const FilterToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const FiltersGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: #d3d3d3 1px solid;
`;

const FilterRow = styled.div<{ $width?: string }>`
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(${(props) => props.$width || "350px"}, 1fr)
  );
  gap: 1rem;
`;

const CheckboxContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
  padding: 0.7rem;
`;

interface AdminReportFiltersProps {
  onFiltersChange?: (filters: AdminReportFilters) => void;
  users?: Users[];
  companies?: Company[];
  companyOrganizations?: Organization[];
}

const AdminReportFiltersComponent: React.FC<AdminReportFiltersProps> = ({
  onFiltersChange,
  users,
  companies,
  companyOrganizations,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);

  const businessIdentifierId = 50;

  const [filters, setSelectedAdminFilters] = useUIStore(
    useShallow((state: UIState) => [
      state.selectedAdminFilters,
      state.setSelectedAdminFilters,
    ]),
  );

  const yearOptions = Array.from({ length: 10 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { label: year.toString(), value: year };
  });

  const quarterOptions = [
    { label: "Q1", value: 1 },
    { label: "Q2", value: 2 },
    { label: "Q3", value: 3 },
    { label: "Q4", value: 4 },
  ];

  const { monthOptions } = useMonths();

  const getStatusText = (status: ReportStatus) => {
    switch (status) {
      case ReportStatus.Draft:
        return t("label-draft");
      case ReportStatus.Sent:
        return t("label-sent");
      case ReportStatus.WaitingForApproval:
        return t("label-waiting-for-approval");
      default:
        return "";
    }
  };

  const statusOptions = [
    { label: getStatusText(ReportStatus.Draft), value: ReportStatus.Draft },
    { label: getStatusText(ReportStatus.Sent), value: ReportStatus.Sent },
    {
      label: getStatusText(ReportStatus.WaitingForApproval),
      value: ReportStatus.WaitingForApproval,
    },
  ];

  const search = (event: AutoCompleteCompleteEvent) => {
    setTimeout(() => {
      let _filteredCompanies;

      if (!event.query.trim().length) {
        _filteredCompanies = [...(companies || [])];
      } else {
        _filteredCompanies = companies?.filter((company) => {
          return (
            company.companyName
              .toLowerCase()
              .includes(event.query.toLowerCase()) ||
            company.identifiers
              .find((id) => id.identifierType.id === 50)
              ?.text?.toLowerCase()
              .includes(event.query.toLowerCase())
          );
        });
      }

      _filteredCompanies = _filteredCompanies?.sort((a, b) =>
        a.companyName.localeCompare(b.companyName, undefined, {
          sensitivity: "base",
        }),
      );

      setFilteredCompanies(_filteredCompanies);
    }, 500);
  };

  const handleCompanyChange = (e: { value: Company | null }) => {
    const company = e.value;
    setSelectedCompany(company);
  };

  const handleCompanySelect = (e: { value: Company }) => {
    const company = e.value;
    setSelectedCompany(company);
    handleFilterChange("company", company);
  };

  useEffect(() => {
    if (filters.company && companies && companies.length > 0) {
      const company = companies.find((c) => c.id === filters.company?.id);
      if (company && company !== selectedCompany) {
        setSelectedCompany(company);
      }
    } else if (!filters.company) {
      setSelectedCompany(null);
    }
  }, [filters.company, companies]);

  const handleFilterChange = (key: keyof AdminReportFilters, value: any) => {
    const updatedFilters = { ...filters, [key]: value };
    setSelectedAdminFilters(updatedFilters);
    onFiltersChange?.(updatedFilters);
  };

  const handleRemoveCompany = () => {
    setSelectedCompany(null);
    const updatedFilters = {
      ...filters,
      company: undefined,
      organization: undefined,
    };
    setSelectedAdminFilters(updatedFilters);
    onFiltersChange?.(updatedFilters);
  };

  const activeFiltersCount = [
    filters.company,
    filters.organization,
    filters.delegatedTo,
    filters.reportStatus,
    filters.year,
    filters.quarter,
    filters.month,
    filters.requiresApproval,
  ].filter(Boolean).length;

  const handleRemoveAllFilters = () => {
    setSelectedCompany(null);
    const clearedFilters: AdminReportFilters = {
      skip: filters.skip ?? 0,
      take: filters.take ?? 50,
    };
    setSelectedAdminFilters(clearedFilters);
    onFiltersChange?.(clearedFilters);
  };

  return (
    <FilterContainer>
      <FilterToggleRow>
        <FilterToggle
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsOpen(!isOpen);
            }
          }}
          tabIndex={0}
          role="button"
          aria-expanded={isOpen}
        >
          <span>
            {t("label-filters")}
            {activeFiltersCount > 0 ? `  (${activeFiltersCount})` : ""}
          </span>
          <ChevronIcon className="pi pi-chevron-down" $isOpen={isOpen} />
        </FilterToggle>
        {activeFiltersCount > 0 && (
          <Button
            label={t("cmd-remove-all-filters")}
            icon="pi pi-filter-slash"
            severity="secondary"
            outlined
            size="small"
            onClick={handleRemoveAllFilters}
          />
        )}
      </FilterToggleRow>

      {isOpen && (
        <FiltersGrid>
          <FilterRow>
            <div>
              <label
                htmlFor="company"
                style={{ display: "block", marginBottom: "0.5rem" }}
              >
                {t("label-active-company")}
              </label>
              <AutoCompleteWrapper>
                <StyledAutoComplete
                  id="company"
                  value={selectedCompany}
                  suggestions={filteredCompanies}
                  dropdown
                  completeMethod={search}
                  field="companyName"
                  onChange={handleCompanyChange}
                  onSelect={handleCompanySelect}
                  placeholder={t("placeholder-select-company")!}
                  itemTemplate={(company: Company) => {
                    const businessId = company.identifiers.find(
                      (id) => id.identifierType.id === businessIdentifierId,
                    )?.text;
                    return businessId
                      ? `${company.companyName} - ${businessId}`
                      : company.companyName;
                  }}
                  selectedItemTemplate={(company: Company) => {
                    const businessId = company.identifiers.find(
                      (id) => id.identifierType.id === businessIdentifierId,
                    )?.text;
                    return businessId
                      ? `${company.companyName} - ${businessId}`
                      : company.companyName;
                  }}
                />
                <RemoveButton
                  onClick={handleRemoveCompany}
                  disabled={!selectedCompany}
                  title={t("label-clear")}
                >
                  <i className="pi pi-times" />
                </RemoveButton>
              </AutoCompleteWrapper>
            </div>

            <div>
              <label
                htmlFor="organization"
                style={{ display: "block", marginBottom: "0.5rem" }}
              >
                {t("header-organization")}
              </label>
              <Dropdown
                id="organization"
                value={filters.organization}
                options={companyOrganizations?.map((org) => ({
                  label: org.organizationName,
                  value: org,
                }))}
                onChange={(e) => handleFilterChange("organization", e.value)}
                placeholder={t("label-select-organization")}
                optionLabel="label"
                style={{ width: "100%" }}
                showClear
              />
            </div>

            <div>
              <label
                htmlFor="delegatedTo"
                style={{ display: "block", marginBottom: "0.5rem" }}
              >
                {t("label-delegated-to")}
              </label>
              <Dropdown
                id="delegatedTo"
                value={filters.delegatedTo}
                options={users}
                onChange={(e) => handleFilterChange("delegatedTo", e.value)}
                placeholder={t("placeholder-select-user")}
                optionLabel="email"
                style={{ width: "100%" }}
                showClear
              />
            </div>

            <div>
              <label
                htmlFor="reportStatus"
                style={{ display: "block", marginBottom: "0.5rem" }}
              >
                {t("label-report-status")}
              </label>
              <Dropdown
                id="reportStatus"
                value={filters.reportStatus}
                options={statusOptions}
                onChange={(e) => handleFilterChange("reportStatus", e.value)}
                placeholder={t("placeholder-select-status")}
                style={{ width: "100%" }}
                showClear
              />
            </div>
          </FilterRow>

          <FilterRow $width="200px">
            <div>
              <label
                htmlFor="year"
                style={{ display: "block", marginBottom: "0.5rem" }}
              >
                {t("label-year")}
              </label>
              <Dropdown
                id="year"
                value={filters.year}
                options={yearOptions}
                onChange={(e) => handleFilterChange("year", e.value)}
                placeholder={t("placeholder-select-year")}
                style={{ width: "100%" }}
                showClear
              />
            </div>

            <div>
              <label
                htmlFor="quarter"
                style={{ display: "block", marginBottom: "0.5rem" }}
              >
                {t("label-quarter")}
              </label>
              <Dropdown
                id="quarter"
                value={filters.quarter}
                options={quarterOptions}
                onChange={(e) => handleFilterChange("quarter", e.value)}
                placeholder={t("placeholder-select-quarter")}
                style={{ width: "100%" }}
                showClear
              />
            </div>

            <div>
              <label
                htmlFor="month"
                style={{ display: "block", marginBottom: "0.5rem" }}
              >
                {t("label-month")}
              </label>
              <Dropdown
                id="month"
                value={filters.month}
                options={monthOptions}
                onChange={(e) => handleFilterChange("month", e.value)}
                placeholder={t("placeholder-select-month")}
                style={{ width: "100%" }}
                showClear
              />
            </div>

            <CheckboxContainer>
              <Checkbox
                inputId="requiresApproval"
                checked={filters.requiresApproval || false}
                onChange={(e) =>
                  handleFilterChange("requiresApproval", e.checked)
                }
              />
              <label htmlFor="requiresApproval" style={{ cursor: "pointer" }}>
                {t("label-requires-approval")}
              </label>
            </CheckboxContainer>
          </FilterRow>
        </FiltersGrid>
      )}
    </FilterContainer>
  );
};

export default AdminReportFiltersComponent;
